import { ComponentProps } from 'react'

import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { TasksValueObjectsTaskStatusEnum } from 'generated/resources/graphql'

import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { dueDateStateData, dueDateStateLabel } from 'modules/inbox_tasks/lib/dueDate'

export const getStatusTag: (
  state: TasksValueObjectsTaskStatusEnum
) => ComponentProps<typeof ResourceHeader>['status'] = (state) => {
  const statusMap: Record<
    TasksValueObjectsTaskStatusEnum,
    {
      text: string
      variant: 'warning' | 'positive' | 'critical'
    }
  > = {
    todo: {
      text: i18n.t('tasks.new_statuses.pending'),
      variant: 'warning',
    },
    in_progress: {
      text: i18n.t('tasks.new_statuses.pending'),
      variant: 'warning',
    },
    done: {
      text: i18n.t('tasks.new_statuses.completed'),
      variant: 'positive',
    },
    discarded: {
      text: i18n.t('tasks.new_statuses.discarded'),
      variant: 'critical',
    },
  }

  return {
    ...statusMap[state],
    label: i18n.t('common.status'),
  }
}

export const getMetadata: (info: {
  createdAt: string
  dueOn?: string
  createdBy?: {
    firstName: string
    lastName: string
    avatarUrl?: string
  }
  assignees?: {
    firstName: string
    lastName: string
    avatarUrl?: string
  }[]
  assigneesTotalCount?: number
}) => ComponentProps<typeof ResourceHeader>['metadata'] = (info) => {
  const displayCreatedAt = i18nDateToString(
    stringToDate(info.createdAt, 'backend_datetime'),
    'ui_abbreviated_month_year'
  )

  const dueDate = info.dueOn ? stringToDate(info.dueOn, 'backend_datetime') : undefined

  const { state, dueDaysBetweenToday } = dueDateStateData(dueDate)

  const dueDateLabel =
    state && state !== 'upcoming' ? dueDateStateLabel(state, dueDaysBetweenToday ?? 0) : undefined

  return [
    {
      label: i18n.t('complaints.status.created'),
      value: {
        formattedDate: displayCreatedAt,
        type: 'date' as const,
      },
    },
    info.createdBy && {
      label: i18n.t('tasks.labels.created_by'),
      value: {
        type: 'avatar' as const,
        text: `${info.createdBy.firstName} ${info.createdBy.lastName}`,
        variant: {
          firstName: info.createdBy.firstName,
          lastName: info.createdBy.lastName,
          src: info.createdBy.avatarUrl,
          type: 'person',
        },
      },
    },
    info.assignees &&
      info.assignees.length > 0 && {
        label: i18n.t('tasks.labels.assignees'),
        value: {
          type: 'list' as const,
          variant: 'person' as const,
          remainingCount: info.assigneesTotalCount
            ? info.assigneesTotalCount - info.assignees.length
            : 0,
          avatars: [
            ...info.assignees.map((assignee) => ({
              type: 'person' as const,
              firstName: assignee.firstName,
              lastName: assignee.lastName,
              src: assignee.avatarUrl,
            })),
          ],
        },
      },
    ...(dueDateLabel
      ? [
          {
            label: i18n.t('common.due_date'),
            value: {
              type: 'date' as const,
              formattedDate: dueDateLabel,
              icon: state === 'overdue' ? ('critical' as const) : ('warning' as const),
            },
          },
        ]
      : []),
  ]
}
